import type { ReactElement, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReportType } from '@/analytics';
import { analytics } from '@/analytics';
import { type ChartTheme, ChartThemeOverride, readChartTheme } from '@/components/charts/theme';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { canvasToPdf, measureKeepTogether } from '@/utils/canvasToPdf';
import { captureElement } from '@/utils/elementCapture';
import { hexToRgbTuple } from '@/utils/exportBackground';

const DEFAULT_CONTAINER_WIDTH = 1200;
const DEFAULT_RENDER_DELAY_MS = 500;

export interface UsePDFExportOptions {
    filename: string;
    content: ReactNode;
    containerWidth?: number;
    renderDelay?: number;
    analyticsExportType?: string;
    analyticsReportType?: ReportType;
}

export interface UsePDFExportReturn {
    triggerDownload: () => void;
    isExporting: boolean;
    PDFContainer: () => ReactElement | null;
}

/**
 * Captures `element` on the light surface and saves it as a PDF, whatever theme is on screen:
 * clients print these. Resolves once the file is handed to the browser.
 */
async function exportElementToPdf(element: HTMLElement, filename: string): Promise<void> {
    // Read from the light frame itself, where the theme's tokens are declared.
    const frame = element.closest('[data-theme]') ?? element;
    const background = readChartTheme('light', frame).surface;
    // Measured before the capture, on the laid-out frame the capture clones.
    const keepTogether = measureKeepTogether(element);
    const canvas = await captureElement(element, { backgroundColor: background });
    const pdf = canvasToPdf(canvas, {
        contentWidthPx: element.offsetWidth,
        background: hexToRgbTuple(background),
        keepTogether,
    });
    await pdf.save(filename, { returnPromise: true });
}

interface LightExportFrameProps {
    width: number;
    captureRef: RefObject<HTMLDivElement | null>;
    children: ReactNode;
}

/**
 * The off-screen copy a PDF is captured from, always in the light theme. Its CSS tokens switch
 * through data-theme; canvas charts cannot read CSS, so they get the light chart theme read from
 * this frame and render once it is known.
 */
function LightExportFrame({ width, captureRef, children }: LightExportFrameProps) {
    const [theme, setTheme] = useState<ChartTheme | null>(null);
    const frameRef = useCallback((node: HTMLDivElement | null) => {
        setTheme(node ? readChartTheme('light', node) : null);
    }, []);

    return (
        <div
            ref={frameRef}
            data-theme="light"
            style={{
                position: 'absolute',
                left: '-9999px',
                top: 0,
                width: `${width}px`,
                background: 'var(--color-bg-surface)',
                // inherited text keeps the page's computed colour unless the frame resets it
                color: 'var(--color-text)',
                colorScheme: 'light',
                zIndex: -1,
            }}>
            {theme && (
                <ChartThemeOverride theme={theme}>
                    <div ref={captureRef}>{children}</div>
                </ChartThemeOverride>
            )}
        </div>
    );
}

export function usePDFExport(options: UsePDFExportOptions): UsePDFExportReturn {
    const {
        filename,
        content,
        containerWidth = DEFAULT_CONTAINER_WIDTH,
        renderDelay = DEFAULT_RENDER_DELAY_MS,
        analyticsExportType,
        analyticsReportType,
    } = options;

    const { t } = useTranslation();
    const [isExporting, setIsExporting] = useState(false);
    const isExportingRef = useRef(false);
    const mountedRef = useRef(true);
    const captureRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            isExportingRef.current = false;
        };
    }, []);

    const triggerDownload = useCallback(() => {
        if (isExportingRef.current) {
            return;
        }

        isExportingRef.current = true;
        setIsExporting(true);

        analytics.exportTriggered({
            exportType: analyticsExportType ?? 'pdf_export',
            fileFormat: 'pdf',
            reportType: analyticsReportType,
        });

        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            const target = captureRef.current;

            // No target when the caller never rendered PDFContainer; nothing is saved then.
            void (target ? exportElementToPdf(target, filename) : Promise.resolve())
                .then(
                    () => {
                        if (analyticsReportType) {
                            analytics.reportDownloaded({
                                reportType: analyticsReportType,
                                fileFormat: 'pdf',
                            });
                        }
                    },
                    () => {
                        ToastError(t('molecules.downloadPDF.failed'), 'download-pdf-error');
                    }
                )
                .finally(() => {
                    isExportingRef.current = false;
                    if (mountedRef.current) {
                        setIsExporting(false);
                    }
                });
        }, renderDelay);
    }, [filename, renderDelay, analyticsExportType, analyticsReportType, t]);

    const PDFContainer = useCallback(() => {
        if (!isExporting) {
            return null;
        }

        return (
            <LightExportFrame width={containerWidth} captureRef={captureRef}>
                {content}
            </LightExportFrame>
        );
    }, [containerWidth, content, isExporting]);

    return {
        triggerDownload,
        isExporting,
        PDFContainer,
    };
}
